<template>
  <div class="quantity">
    <button
      v-add-zuko-tag
      class="quantity__btn is-minus"
      :disabled="isDecDisabled"
      :data-test="`${dataTest}_decr`"
      @click="decrement"
    />
    <div class="quantity__input-container">
      <span
        class="quantity__input-label"
        :class="{ 'unfocused': isInputInFocus }"
      >
        {{ inputLabel }}
      </span>
      <input
        v-add-zuko-tag
        class="quantity__input"
        :class="{ 'focused': isInputInFocus }"
        type="number"
        :data-test="dataTest"
        :min="min"
        :max="max === Infinity ? undefined : max"
        :value="qty"
        @keypress="onKeyPress"
        @input="inputVal"
        @paste.prevent="onPaste"
        @blur="onBlur"
        @focus="onFocus"
      >
    </div>
    <button
      v-add-zuko-tag
      class="quantity__btn is-plus"
      :disabled="isIncDisabled"
      :data-test="`${dataTest}_incr`"
      @click="increment"
    />
  </div>
</template>

<script>
import { isFinite } from 'lodash';

export default {
  name: 'quantity',
  props: {
    value: {
      type: Number,
      required: true,
    },
    min: {
      type: [Number, String],
      default: 0,
      validator: value => +value >= 0,
    },
    max: {
      type: [Number, String],
      default: Number.POSITIVE_INFINITY,
    },
    step: {
      type: [Number, String],
      default: 1,
    },
    maxWithPlus: {
      type: Boolean,
      default: false,
    },
    dataTest: {
      type: String,
      required: true,
    },
  },
  data () {
    let qty = +this.value;
    if (qty < +this.min) qty = +this.min;
    if (isFinite(+this.max) && qty > +this.max) qty = +this.max;
    this.$emit('input', qty);
    return {
      qty,
      isInputInFocus: false,
    };
  },
  computed: {
    isDecDisabled () { return this.qty <= +this.min; },
    isIncDisabled () { return isFinite(+this.max) && this.qty >= +this.max; },
    inputLabel () {
      if (this.maxWithPlus && this.qty === +this.max) {
        return `${this.qty}+`;
      }
      return this.qty;
    },
  },
  methods: {
    onBlur ({ target }) {
      this.isInputInFocus = false;
      if (target.value === '') {
        this.qty = 0;
      } else if (+target.value < +this.min) {
        this.qty = +this.min;
      } else if (isFinite(+this.max) && +target.value > +this.max) {
        this.qty = +this.max;
        this.$emit('max-limit');
      } else {
        this.qty = +this.qty;
      }

      target.value = this.qty;
      this.$emit('input', this.qty);
    },
    onFocus (e) {
      e.target.select();
      this.isInputInFocus = true;
    },
    onKeyPress (e) {
      const charCode = e.which ? e.which : e.keyCode;

      if (charCode < 48 || charCode > 57) {
        e.preventDefault();
      }
    },
    onPaste ({
      clipboardData,
      target,
    }) {
      const pastedValue = clipboardData.getData('text/plain');

      const numbers = pastedValue.match(/\d+/g);
      const newValue = (numbers && numbers.length > 0) ? +numbers[0] : null;

      if (
        newValue === null ||
        (isFinite(+this.min) && newValue < +this.min) ||
        (isFinite(+this.max) && newValue > +this.max)
      ) {
        this.$emit('max-limit');
        return;
      }

      this.qty = newValue;
      target.value = this.qty;
      this.$emit('input', this.qty);
    },
    inputVal (e) {
      this.qty = +e.target.value;
      this.$emit('input', this.qty);
    },
    increment () {
      this.qty += +this.step;

      if (isFinite(+this.max) && this.qty > +this.max) {
        this.qty = +this.max;
      }

      this.$emit('input', this.qty);
    },
    decrement () {
      this.qty -= +this.step;

      if (isFinite(+this.min) && this.qty < +this.min) {
        this.qty = +this.min;
      }

      this.$emit('input', this.qty);
    },
  },
};
</script>

<style lang="scss" scoped>
$fSize: 16px;

.quantity {
  display: flex;
  max-width: 350px;

  &__btn {
    flex-shrink: 0;
    padding: 0;
    width: 56px;
    height: 56px;
    background: var(--v-primary-base);
    border: none;
    border-radius: 50%;
    position: relative;
    transition: background-color 0.15s ease-in-out;

    &:active {
      top: 2px;
    }

    &::before,
    &::after {
      content: '';
      display: inline-block;
      background-color: #fff;
      position: absolute;
    }

    &::before {
      width: 14px;
      height: 2px;
      top: calc(50% - 1px);
      left: calc(50% - 7px);
    }

    &:focus,
    &:hover {
      outline: none;
      box-shadow: inset 0 0 0 2px var(--v-navy-dark-base, $clr-navy-dark);
    }

    &:disabled:focus {
      box-shadow: none;
    }

    &.is-plus::after {
      width: 2px;
      height: 14px;
      top: calc(50% - 7px);
      left: calc(50% - 1px);
    }

    &:disabled {
      background: #999;
      pointer-events: none;
    }
  }

  &__input-container {
    position: relative;
    width: 100%;
    margin: 0 32px;
  }

  &__input-label,
  &__input {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    text-align: center;
    font-weight: 500;
    color: #031b30;
  }

  &__input-label {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: transparent;
    font-size: $fSize;
    vertical-align: middle;
    pointer-events: none;
    line-height: 3.5em;

    &.unfocused {
      font-size: 0;
    }
  }

  &__input {
    display: block;
    flex-grow: 1;
    border: 1px solid var(--v-navy-dark-base, $clr-navy-dark);
    font-size: 0;
    line-height: 1.5;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    &::-ms-clear {
      display: none;
    }

    &.focused {
      font-size: $fSize;
    }
  }
}
</style>
